// Turns raw IRS acknowledgement errors into readable messages with guidance.
// Mirrors the backend email classification in api/tasks.py (_classify_rejection):
//   fixable   — a client-data problem (PIN, name, address, VIN, bank info...)
//   duplicate — F2290-024/025/026 / "previously accepted" duplicate filings
//   technical — schema/software issues on our side, and anything unrecognized

// xpath fragment -> friendly field name (checked in order, most specific first)
const FIELD_BY_XPATH = [
  ["TaxpayerPIN", "Signing PIN"],
  ["PractitionerPINGrp", "Signing PIN"],
  ["PersonNm", "signing authority name"],
  ["BusinessNameControlTxt", "business name"],
  ["BusinessNameLine1Txt", "business name"],
  ["AddressLine1Txt", "street address"],
  ["CityNm", "city"],
  ["StateAbbreviationCd", "state"],
  ["ZIPCd", "ZIP code"],
  ["RoutingTransitNum", "bank routing number"],
  ["BankAccountNum", "bank account number"],
  ["PhoneNum", "phone number"],
  ["VIN", "vehicle VIN"],
  ["EIN", "EIN"],
];

// Company-profile fields are fixed on the company page; VINs inside the form.
const FORM_FIELDS = new Set(["vehicle VIN"]);

export function classifyAckError(err) {
  const rule = String(err?.ruleNum || "").trim();
  const msg = String(err?.errorMessageTxt || "").trim();
  const cat = String(err?.errorCategoryCd || "").trim();
  const xp = String(err?.xpathContentTxt || "");
  const low = (msg + " " + xp).toLowerCase();

  if (
    rule.startsWith("F2290-024") ||
    rule.startsWith("F2290-025") ||
    rule.startsWith("F2290-026") ||
    low.includes("previously accepted")
  ) {
    return { bucket: "duplicate", field: null };
  }
  if (
    low.includes("softwareid") ||
    low.includes("returnversion") ||
    low.includes("schema version")
  ) {
    return { bucket: "technical", field: null };
  }
  const hit = FIELD_BY_XPATH.find(([frag]) => xp.includes(frag));
  if (hit) return { bucket: "fixable", field: hit[1] };
  if (cat === "XML Error") return { bucket: "technical", field: null };
  return { bucket: "technical", field: null };
}

function guidanceFor(bucket, field) {
  if (bucket === "fixable") {
    const fld = field || "the affected information";
    if (FORM_FIELDS.has(fld)) {
      return (
        "This was caused by a problem with the " + fld + ". Please correct the " +
        fld + " in this form's vehicle list and resubmit the form. Once fixed, " +
        "the IRS should accept it."
      );
    }
    return (
      "This was caused by a problem with your " + fld + ". Please open the " +
      "company in your dashboard, correct the " + fld + ", and resubmit the " +
      "form. Once fixed, the IRS should accept it."
    );
  }
  if (bucket === "duplicate") {
    return (
      "The IRS shows a Form 2290 was already accepted for this vehicle and tax " +
      "period, so it cannot be filed again. If you did not already file this " +
      "elsewhere, please contact support@2290pay.org."
    );
  }
  return (
    "This is a technical issue on our side — not a problem with your " +
    "information. Our team is already working on it. You don't need to do " +
    "anything; we'll resubmit your return and email you once it's accepted."
  );
}

function titleFor(bucket, field) {
  if (bucket === "fixable") return "Problem with your " + (field || "information");
  if (bucket === "duplicate") return "Already filed with the IRS (duplicate)";
  return "Technical issue on our side";
}

/**
 * explainAck(ackJson, businessRules?) ->
 *   { status: 'Accepted'|'Rejected'|null, errors: [{title, reason, guidance, bucket}] }
 * businessRules: optional { [ruleNum]: officialText } used when the ack has no
 * errorMessageTxt for a rule.
 */
export function explainAck(ackJson, businessRules) {
  const ack = (ackJson?.acknowledgements || [])[0] || {};
  const status = ack?.acceptanceStatusTxt || null;
  const grp = ack?.validationErrorList?.validationErrorGrp || [];

  const errors = [];
  const seen = new Set();
  for (const e of Array.isArray(grp) ? grp : []) {
    const { bucket, field } = classifyAckError(e);
    const rule = String(e?.ruleNum || "").trim();
    const msg =
      String(e?.errorMessageTxt || "").trim() ||
      String(businessRules?.[rule] || "").trim() ||
      String(e?.xpathContentTxt || "").trim();
    const reason = rule && msg ? rule + " — " + msg : rule || msg;
    const key = bucket + "|" + reason;
    if (seen.has(key)) continue;
    seen.add(key);
    errors.push({
      title: titleFor(bucket, field),
      reason,
      guidance: guidanceFor(bucket, field),
      bucket,
    });
  }
  return { status, errors };
}
